import React, { useRef, useState } from "react";
import { GROUP_BY_OPTIONS, SORT_OPTIONS } from "../constants";

// ─── WorkFilters ───────────────────────────────────────────────────────────────
// The Work page filter row, arranged left to right:
//   [Filters ▾] [Sort ▾] [Group ▾] │ [Presets ▾] [Save] ··· [🔍 Search…]
// Sort is a split button: icon toggles direction, rest opens picker.
// Group has an icon inside the box for visual parity (no action).
// Owner / Client / Service / Work type collapse behind the Filters button.

const PIPE_CLASS = "border-l border-neutral-700 h-6 mx-2 self-center";
const SEARCH_INPUT_CLASS =
  "text-sm h-9 border border-gray-200 dark:border-gray-700 rounded-lg pl-8 pr-8 bg-white dark:bg-gray-900 text-gray-700 dark:text-gray-300 placeholder-gray-400 dark:placeholder-gray-500 w-48";

// ─── Trigger buttons ───────────────────────────────────────────────────────────
const TRIGGER_BASE =
  "flex items-center gap-1.5 h-9 rounded-lg px-3 text-sm border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-gray-700 dark:text-gray-300 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700";
const TRIGGER_ACTIVE =
  "flex items-center gap-1.5 h-9 rounded-lg px-3 text-sm border border-blue-500/50 dark:border-blue-500/50 bg-blue-50/50 dark:bg-blue-900/20 text-gray-700 dark:text-gray-300 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700";
const triggerClass = (isActive) => (isActive ? TRIGGER_ACTIVE : TRIGGER_BASE);

// Sort split button outer container. overflow-hidden clips the inner zones'
// hover backgrounds to the parent's rounded corners (Item 3 — hover was
// bleeding past the rounded-lg bounds on the left side).
const SORT_SPLIT_BASE =
  "flex items-center h-9 rounded-lg overflow-hidden border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-gray-700 dark:text-gray-300";
const SORT_SPLIT_ACTIVE =
  "flex items-center h-9 rounded-lg overflow-hidden border border-blue-500/50 dark:border-blue-500/50 bg-blue-50/50 dark:bg-blue-900/20 text-gray-700 dark:text-gray-300";
const sortSplitClass = (isActive) => (isActive ? SORT_SPLIT_ACTIVE : SORT_SPLIT_BASE);

// ─── Dropdown panel (shared by all dropdowns) ─────────────────────────────────
const DROPDOWN_PANEL =
  "absolute z-30 mt-1 min-w-[10rem] max-h-72 overflow-y-auto rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 shadow-lg";
const DROPDOWN_ITEM = (isActive) =>
  `flex items-center w-full px-3 py-2 text-xs hover:bg-gray-100 dark:hover:bg-gray-800 cursor-pointer ${
    isActive ? "text-blue-500 font-medium" : "text-gray-700 dark:text-gray-300"
  }`;

// ─── Inline SVG icons ──────────────────────────────────────────────────────────
// lucide-react is not a project dependency — all icons are inline SVG.

// Sort / arrows up-down icon (the direction toggle, shown inside the sort
// button). Direction-aware visual weight (Item 2 — sort arrow visual weight):
// the arrow matching the active direction is bold/dark with a thicker stroke,
// the opposite arrow is muted, so users can tell which direction is applied.
const SortIcon = ({ direction, ...props }) => {
  const asc = direction === "asc";
  const activeClass = "text-gray-900 dark:text-gray-100";
  const mutedClass = "text-gray-300 dark:text-gray-600";
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" {...props}>
      {/* up arrow — bold/dark when ascending, muted otherwise */}
      <path d="m7 9 5-5 5 5" stroke="currentColor" strokeWidth={asc ? 2.5 : 2} className={asc ? activeClass : mutedClass} />
      {/* down arrow — bold/dark when descending, muted otherwise */}
      <path d="m7 15 5 5 5-5" stroke="currentColor" strokeWidth={asc ? 2 : 2.5} className={asc ? mutedClass : activeClass} />
    </svg>
  );
};

// Chevron-down icon (the dropdown opener, shown inside both sort and group buttons).
const ChevronDown = ({ size = 10, ...props }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" className="opacity-60" {...props}>
    <path d="M6 9l6 6 6-6" />
  </svg>
);

// Layout-list icon (inside the Group button for visual parity with Sort).
const LayoutListIcon = ({ size = 12, ...props }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" {...props}>
    <line x1="8" x2="21" y1="6" y2="6" />
    <line x1="8" x2="21" y1="12" y2="12" />
    <line x1="8" x2="21" y1="18" y2="18" />
    <line x1="3" x2="3.01" y1="6" y2="6" />
    <line x1="3" x2="3.01" y1="12" y2="12" />
    <line x1="3" x2="3.01" y1="18" y2="18" />
  </svg>
);

// Funnel icon (inside the Filters button).
const FunnelIcon = ({ size = 14, ...props }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" {...props}>
    <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" />
  </svg>
);

// Bookmark icon (Save preset button).
const BookmarkIcon = ({ size = 16, ...props }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}>
    <path d="m19 21-7-4-7 4V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2v16z" />
    <path d="M12 7v6" />
    <path d="M9 10h6" />
  </svg>
);

// Search icon + X icon for the search input.
const SearchIcon = ({ size = 14, ...props }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" {...props}>
    <circle cx="11" cy="11" r="8" />
    <line x1="21" y1="21" x2="16.65" y2="16.65" />
  </svg>
);
const XIcon = ({ size = 14, ...props }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" {...props}>
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);

const WorkFilters = ({
  filterOwner, setFilterOwner,
  filterService, setFilterService,
  filterPipeline, setFilterPipeline,
  clientId, setClientId,
  allFilterOptions, workTypeOptions,
  groupBy, setGroupBy,
  sortField, setSortField,
  sortDirection, setSortDirection,
  includeDone, setIncludeDone,
  searchQuery, setSearchQuery,
  savedViews, selectedViewId, setSelectedViewId,
  presetDropdownOpen, setPresetDropdownOpen,
  confirmingDeleteId, setConfirmingDeleteId,
  handleDeletePreset, setShowSaveModal,
  hasActiveFilters, handleClearFilters, setActiveBucket, activeBucket,
}) => {
  const [searchInput, setSearchInput] = useState(searchQuery || "");
  const searchTimerRef = useRef(null);

  const [filtersOpen, setFiltersOpen] = useState(false);
  const [sortOpen, setSortOpen] = useState(false);
  const [groupOpen, setGroupOpen] = useState(false);
  // Mobile (below md): the individual filter controls collapse behind the
  // "Filters" button. When true, the expanded panel (selects + sort/group/presets)
  // renders below the Filters + Search row. Desktop ignores this — controls stay
  // inline there (work-page-fixes §B).
  const [mobileFiltersExpanded, setMobileFiltersExpanded] = useState(false);

  const activeFilterCount =
    [filterOwner, clientId, filterService, filterPipeline].filter(Boolean).length +
    (includeDone ? 1 : 0);

  // Owner filter is structurally fixed on the "Unowned" (nobody owns it) and
  // "Mine" / waiting_on_me (current user owns it) buckets — an owner filter
  // there is contradictory, so the dropdown is disabled + dimmed. WorkPage
  // also clears any stale filterOwner on the switch (work-page-fixes §B).
  const ownerDisabled = activeBucket === "unowned" || activeBucket === "waiting_on_me";

  // Label map for the sort dropdown trigger. Derived from SORT_OPTIONS so the
  // trigger text matches the option the user picked in the dropdown (Item 3 —
  // sort option label display, e.g. "days_in_status" → "Days in status" instead
  // of the raw key). Extra fallback entries cover sort keys that may be set by
  // saved presets but aren't in the dropdown (created_at, updated_at).
  const SORT_LABELS = {
    ...SORT_OPTIONS.reduce((acc, opt) => { acc[opt.key] = opt.label; return acc; }, {}),
    created_at: "Created",
    updated_at: "Updated",
  };
  // Defensive (Item 5 — Remove Presets from sort dropdown): never let an
  // unrecognized/legacy preset key (e.g. a stray "Presets" string) leak into
  // the trigger as a raw key. If sortField isn't a known sort key, show the
  // generic "Sort" label so the trigger only ever displays valid sort names.
  const sortLabel = SORT_LABELS[sortField] || "Sort";

  // Reusable "filter" selects block (owner / client / service / pipeline /
  // include-done + clear-all). Rendered in the desktop Filters dropdown AND in the
  // mobile expanded panel so the same controls are reachable on both breakpoints
  // without duplicating the JSX (work-page-fixes §B).
  const filterSelects = (
    <>
      <select
        value={filterOwner || ""}
        onChange={(e) => setFilterOwner(e.target.value || null)}
        className={`${triggerClass(!!filterOwner)} w-full ${ownerDisabled ? "opacity-50 cursor-not-allowed" : ""}`}
        disabled={ownerDisabled}
        aria-label="Owner"
      >
        <option value="">All owners</option>
        {(allFilterOptions?.owners || []).map((o) => (
          <option key={o.id} value={o.id}>{o.name}</option>
        ))}
      </select>
      <select
        value={clientId || ""}
        onChange={(e) => setClientId(e.target.value || null)}
        className={`${triggerClass(!!clientId)} w-full`}
        aria-label="Client"
      >
        <option value="">All clients</option>
        {(allFilterOptions?.clients || []).map((c) => (
          <option key={c.id} value={c.id}>{c.name}</option>
        ))}
      </select>
      <select
        value={filterService || ""}
        onChange={(e) => setFilterService(e.target.value || null)}
        className={`${triggerClass(!!filterService)} w-full`}
        aria-label="Service"
      >
        <option value="">All services</option>
        {(allFilterOptions?.services || []).map((s) => (
          <option key={s.code} value={s.code}>{s.label}</option>
        ))}
      </select>
      <select
        value={filterPipeline || ""}
        onChange={(e) => setFilterPipeline(e.target.value || null)}
        className={`${triggerClass(!!filterPipeline)} w-full`}
        aria-label="Work type"
      >
        <option value="">All work types</option>
        {(workTypeOptions || []).map((wt) => (
          <option key={wt.value} value={wt.value}>{wt.label}</option>
        ))}
      </select>
      <div className="border-t border-gray-200 dark:border-gray-700 my-2" />
      <div className="flex items-center justify-between">
        <span className="text-sm text-gray-700 dark:text-gray-300">Include completed</span>
        <button
          type="button"
          onClick={() => setIncludeDone(!includeDone)}
          role="switch"
          aria-checked={includeDone}
          aria-label="Include completed tasks"
          className={`relative inline-flex items-center w-8 h-4 rounded-full transition-colors ${includeDone ? "bg-blue-500" : "bg-gray-300 dark:bg-gray-600"}`}
        >
          <span className={`inline-block w-3 h-3 rounded-full bg-white transition-transform ${includeDone ? "translate-x-4" : "translate-x-1"}`} />
        </button>
      </div>
      <button
        type="button"
        onClick={() => {
          setFilterOwner(null);
          setClientId(null);
          setFilterService(null);
          setFilterPipeline(null);
          setIncludeDone(false);
        }}
        className="text-xs text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 cursor-pointer mt-1 text-right"
      >
        Clear all
      </button>
    </>
  );

  return (
    <div className="flex items-center gap-2 flex-wrap">

      {/* ── 1. Filters button ──────────────────────────────────────────────── */}
      <div className="relative">
        <button
          type="button"
          onClick={() => { setFiltersOpen((o) => !o); setMobileFiltersExpanded((o) => !o); }}
          className={triggerClass(activeFilterCount > 0)}
          aria-label="Filters"
        >
          <FunnelIcon />
          Filters{activeFilterCount > 0 ? ` (${activeFilterCount})` : ""}
          <ChevronDown />
        </button>
        {filtersOpen && (
          <>
            <div className="hidden md:block fixed inset-0 z-20" onClick={() => setFiltersOpen(false)} />
            <div className={`${DROPDOWN_PANEL} left-0 w-64 p-3 hidden md:flex flex-col gap-2`}>
              {filterSelects}
            </div>
          </>
        )}
      </div>

      {/* Mobile expanded panel — filter selects (owner/client/service/pipeline/
          include-done). The desktop Filters dropdown reuses the same `filterSelects`
          block; on mobile this renders them in the expanded panel below the
          Filters + Search row (work-page-fixes §B). */}
      <div className={`${mobileFiltersExpanded ? "flex flex-col w-full order-2 gap-2" : "hidden"} md:hidden`}>
        {filterSelects}
      </div>

      {/* Mobile expanded controls wrapper. On desktop `md:contents` so its children
          flow inline exactly as before; on mobile a full-width stacked block shown
          only when expanded (work-page-fixes §B). */}
      <div className={`${mobileFiltersExpanded ? "flex flex-col w-full order-3 gap-2" : "hidden"} md:contents`}>
      {/* ── 2. Sort split button (always active — a sort is always applied) ── */}
      <div className="relative">
        <div className={sortSplitClass(true)}>
          {/* Left zone — sort icon toggles direction. Always enabled (Item 3):
              urgency ignores direction in the comparator, but the toggle stays
              responsive and the chosen direction applies immediately to any
              directional sort — no need to switch fields first. */}
          <div
            role="button"
            tabIndex={0}
            onClick={(e) => {
              e.stopPropagation();
              setSortDirection((prev) => (prev === "asc" ? "desc" : "asc"));
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.stopPropagation();
                setSortDirection((prev) => (prev === "asc" ? "desc" : "asc"));
              }
            }}
            className="flex items-center justify-center h-full px-2.5 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700"
            title={sortDirection === "asc" ? "Ascending — click to reverse" : "Descending — click to reverse"}
          >
            <SortIcon direction={sortDirection} />
          </div>
          {/* Visual divider */}
          <div className="w-px bg-gray-200 dark:bg-gray-700 self-stretch my-1.5" />
          {/* Right zone — label + chevron opens field picker */}
          <div
            role="button"
            tabIndex={0}
            onClick={() => setSortOpen((o) => !o)}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") setSortOpen((o) => !o);
            }}
            className="flex items-center gap-1.5 h-full px-3 pr-2 text-sm cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 rounded-r-lg select-none"
            title="Change sort field"
            aria-label="Open sort field picker"
          >
            <span>{sortLabel}</span>
            <ChevronDown size={10} />
          </div>
        </div>
        {/* Sort field picker dropdown */}
        {sortOpen && (
          <>
            <div className="fixed inset-0 z-20" onClick={() => setSortOpen(false)} />
            <div className={`${DROPDOWN_PANEL} left-0`}>
              {SORT_OPTIONS.map((opt) => (
                <button
                  key={opt.key}
                  type="button"
                  onClick={() => {
                    setSortField(opt.key);
                    setSortOpen(false);
                  }}
                  className={DROPDOWN_ITEM(sortField === opt.key)}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </>
        )}
      </div>

      {/* ── 3. Group button (icon + value + chevron) ─────────────────────── */}
      <div className="relative">
        {/* groupBy is never null — "none" (No Grouping) is a valid grouping mode,
            not an unset state — so the Group trigger is always active (blue).
            Unlike the Owner/Client filters, which can be null and show gray when
            unset, the Group control always holds a definite value. */}
        <div className={triggerClass(true)}>
          {/* Icon — decorative, same visual weight as the sort icon */}
          <LayoutListIcon size={12} className="opacity-60 shrink-0" />
          {/* Label + chevron — whole button opens the dropdown */}
          <button
            type="button"
            onClick={() => setGroupOpen((o) => !o)}
            className="flex items-center gap-1.5 h-full cursor-pointer bg-transparent border-none text-inherit p-0 text-sm select-none"
            aria-label="Group"
          >
            <span>{GROUP_BY_OPTIONS.find((o) => o.key === (groupBy || "status"))?.label || "Group"}</span>
            <ChevronDown />
          </button>
        </div>
        {groupOpen && (
          <>
            <div className="fixed inset-0 z-20" onClick={() => setGroupOpen(false)} />
            <div className={`${DROPDOWN_PANEL} left-0`}>
              {GROUP_BY_OPTIONS.map((opt) => (
                <button
                  key={opt.key}
                  type="button"
                  onClick={() => {
                    setGroupBy(opt.key);
                    setGroupOpen(false);
                  }}
                  className={DROPDOWN_ITEM((groupBy || "status") === opt.key)}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </>
        )}
      </div>

      {hasActiveFilters && (
        <button
          type="button"
          onClick={handleClearFilters}
          className="text-xs text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 px-2"
        >
          Clear
        </button>
      )}

      {/* ── Pipe separator ─────────────────────────────────────────────────── */}
      <div className={`${PIPE_CLASS} hidden md:block`} />

      {/* ── 4. Presets dropdown + Save button ───────────────────────────────── */}
      <div className="flex items-center gap-2">
        <div className="relative">
          {/* Trigger + inline clear (×) button (Item 5 — preset deselection).
              The × only renders when a preset is selected, so users can
              deselect without opening the dropdown. It mirrors the search
              input's × button styling already used in this file. */}
          <div className={triggerClass(!!selectedViewId)}>
            <button
              type="button"
              onClick={() => setPresetDropdownOpen((o) => !o)}
              className="flex items-center gap-1.5 h-full bg-transparent border-none text-inherit p-0 cursor-pointer"
              aria-label="Open presets"
            >
              {selectedViewId
                ? (savedViews.find((v) => v.id === selectedViewId)?.name || "Presets")
                : "Presets"}
              <ChevronDown />
            </button>
            {selectedViewId && (
              <button
                type="button"
                onClick={() => setSelectedViewId(null)}
                aria-label="Clear preset"
                title="Clear preset"
                className="flex items-center justify-center ml-0.5 -mr-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              >
                <XIcon size={12} />
              </button>
            )}
          </div>
          {presetDropdownOpen && (
            <>
              <div className="fixed inset-0 z-20" onClick={() => setPresetDropdownOpen(false)} />
              <div className={`${DROPDOWN_PANEL} right-0 w-56`}>
                <button
                  type="button"
                  onClick={() => {
                    setSelectedViewId(null);
                    setPresetDropdownOpen(false);
                  }}
                  className="flex items-center justify-between w-full px-3 py-2 text-xs text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 cursor-pointer"
                  aria-label="Clear preset selection"
                >
                  <span>Clear preset</span>
                </button>
                {/* Visual separator — sections the "Clear preset" meta-action
                    away from the actual preset list so it reads as an action,
                    not as one of the options. */}
                <div className="h-px bg-gray-200 dark:bg-gray-700 mx-1" />
                {savedViews.length === 0 && (
                  <div className="px-3 py-2 text-xs text-gray-400 dark:text-gray-500">No saved presets</div>
                )}
                {savedViews.map((v) => (
                  <div
                    key={v.id}
                    role="button"
                    tabIndex={0}
                    onClick={() => {
                      setSelectedViewId(v.id);
                      if (v?.filters) {
                        if (v.filters.group_by) setGroupBy(v.filters.group_by);
                        setFilterOwner(v.filters?.p_owner_id || null);
                        setFilterService(v.filters?.p_service || null);
                        setFilterPipeline(v.filters?.p_work_type || null);
                        setIncludeDone(v.filters?.p_include_done || false);
                        if (v.filters?.p_bucket) setActiveBucket(v.filters.p_bucket);
                      }
                      setPresetDropdownOpen(false);
                    }}
                    className={DROPDOWN_ITEM(false)}
                  >
                    <span className="truncate">
                      {v.name}{v.is_mine ? "" : " (shared)"}
                    </span>
                    {v.is_mine && (
                      confirmingDeleteId === v.id ? (
                        <span className="flex items-center gap-1 ml-auto">
                          <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); handleDeletePreset(v.id); }}
                            className="text-xs text-red-500 dark:text-red-400 hover:text-red-600 dark:hover:text-red-300"
                          >
                            Delete?
                          </button>
                          <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); setConfirmingDeleteId(null); }}
                            className="text-xs text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
                          >
                            Cancel
                          </button>
                        </span>
                      ) : (
                        <button
                          type="button"
                          onClick={(e) => { e.stopPropagation(); setConfirmingDeleteId(v.id); }}
                          className="ml-auto text-gray-400 hover:text-red-500 dark:text-gray-500 dark:hover:text-red-400 text-xs px-1"
                          aria-label={`Delete preset ${v.name}`}
                        >
                          ×
                        </button>
                      )
                    )}
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
        <button
          type="button"
          onClick={() => setShowSaveModal(true)}
          title="Save preset"
          aria-label="Save preset"
          className="h-9 px-2 flex items-center rounded-lg text-gray-500 hover:text-gray-700 hover:bg-gray-100 dark:text-gray-400 dark:hover:text-gray-200 dark:hover:bg-gray-700 transition-colors"
        >
          <BookmarkIcon size={16} />
        </button>
      </div>
      </div>{/* /mobile expanded controls wrapper */}

      {/* ── 5. Search (debounced 300ms, Enter for immediate, × to clear) ────── */}
      <div className="relative ml-auto order-1 md:order-none">
        <SearchIcon size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-500 dark:text-gray-400 pointer-events-none" />
        <input
          type="text"
          placeholder="Search work…"
          value={searchInput}
          onChange={(e) => {
            const value = e.target.value;
            setSearchInput(value);
            if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
            searchTimerRef.current = setTimeout(() => {
              setSearchQuery(value);
              searchTimerRef.current = null;
            }, 300);
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              if (searchTimerRef.current) {
                clearTimeout(searchTimerRef.current);
                searchTimerRef.current = null;
              }
              setSearchQuery(searchInput);
            }
          }}
          className={SEARCH_INPUT_CLASS}
        />
        {searchInput && (
          <button
            type="button"
            onClick={() => {
              if (searchTimerRef.current) {
                clearTimeout(searchTimerRef.current);
                searchTimerRef.current = null;
              }
              setSearchInput("");
              setSearchQuery("");
            }}
            aria-label="Clear search"
            className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
          >
            <XIcon size={14} />
          </button>
        )}
      </div>
    </div>
  );
};

export default WorkFilters;
